/**
 * Attachments — master spec §6.13.
 *
 * **The server issues credentials; the browser moves the bytes.** The Lambda's
 * 6 MB synchronous request payload and 6 MB buffered response (§3b) make
 * anything else impossible: nothing large may be POSTed through the app, and a
 * download is a redirect rather than proxied bytes.
 */

import { z } from 'zod';
import {
  attachmentEntityTypeSchema,
  storedAttachmentEntityTypeSchema,
  uploadStateSchema,
} from './enums.js';
import { exportAttestationSchema } from './export-control.js';
import { dataEnvelope } from './http.js';
import { timestampString, uuid } from './fields.js';

/**
 * **100 MB is a convention, not a limit** (§6.13 [R3]). A single presigned POST
 * handles up to 5 GB; AWS merely recommends multipart above roughly 100 MB for
 * throughput and retry granularity. It is a constant rather than a magic
 * number, and the POST policy's upper bound is DERIVED from the configurable
 * `app_settings` cap so the two cannot drift.
 */
export const MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024;

/** 16 MB parts, as §6.13 specifies. */
export const MULTIPART_PART_SIZE_BYTES = 16 * 1024 * 1024;

/** Presigned POST: 5 minutes. Short, because the browser posts immediately. */
export const PRESIGNED_POST_TTL_SECONDS = 5 * 60;

/**
 * Presigned `UploadPart`: **6 hours** (§6.13 [R4]). A 1 GB upload on a slow
 * link outlives a short expiry mid-transfer; the client re-requests expired
 * part URLs rather than restarting.
 */
export const PRESIGNED_PART_TTL_SECONDS = 6 * 60 * 60;

/** Presigned GET: 60 seconds. It is followed immediately by a redirect. */
export const PRESIGNED_GET_TTL_SECONDS = 60;

/**
 * The sweeper only touches rows older than this (§6.13 [R4]), and it matches
 * the bucket's `AbortIncompleteMultipartUpload` lifecycle rule so the two agree
 * on when an upload is abandoned.
 */
export const SWEEPER_MIN_AGE_HOURS = 24;

/**
 * **Both `presign` and `multipart` require the §8 attestation** and write an
 * `attachment_attested` event. Multipart is the path carrying the large
 * technical documents §8 item 7 exists for, so it is the worst place for the
 * control to be implicit.
 */
const uploadRequestCore = z.object({
  entityType: attachmentEntityTypeSchema,
  entityId: uuid,
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1).max(200),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(5 * 1024 * 1024 * 1024),
});

export const presignAttachmentSchema = uploadRequestCore.merge(exportAttestationSchema);
export type PresignAttachment = z.infer<typeof presignAttachmentSchema>;

export const startMultipartSchema = uploadRequestCore.merge(exportAttestationSchema);

/**
 * Re-request part URLs for an upload already in flight. No attestation: it was
 * taken when the upload started, and the row it belongs to is already written.
 */
export const multipartPartsSchema = z.object({
  attachmentId: uuid,
  partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(500),
});

export const completeMultipartSchema = z.object({
  parts: z
    .array(z.object({ partNumber: z.number().int().min(1).max(10_000), etag: z.string().min(1) }))
    .min(1)
    .max(10_000),
});

export const presignedPostSchema = z.object({
  kind: z.literal('presigned_post'),
  attachmentId: uuid,
  url: z.string(),
  fields: z.record(z.string()),
  expiresInSeconds: z.number().int(),
});

export const multipartStartSchema = z.object({
  kind: z.literal('multipart'),
  attachmentId: uuid,
  uploadId: z.string(),
  partSizeBytes: z.number().int(),
  partCount: z.number().int(),
  /** Issued in batches; the client re-requests expired ones (§6.13 [R4]). */
  partUrls: z.array(z.object({ partNumber: z.number().int(), url: z.string() })),
  expiresInSeconds: z.number().int(),
});

export const attachmentRowSchema = z.object({
  id: uuid,
  // Read back, so it may name a strategy image too (`account_strategy`).
  entity_type: storedAttachmentEntityTypeSchema,
  entity_id: uuid,
  file_name: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int().nullable(),
  upload_state: uploadStateSchema,
  uploaded_by: uuid,
  uploaded_at: timestampString,
});

export type AttachmentRow = z.infer<typeof attachmentRowSchema>;

export const attachmentListResponseSchema = dataEnvelope(
  z.object({ items: z.array(attachmentRowSchema) }),
);

/**
 * The key is **server-computed and the client never influences it**:
 * `attachments/{entity_type}/{entity_id}/{uuid}/{sanitized_file_name}`.
 *
 * Sanitisation is here rather than in the route because the same function must
 * produce the key the presign signs and the key the confirm HEADs — two copies
 * of "strip the path separators" is how those drift.
 */
export function sanitizeFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? 'file';
  const cleaned = base
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/^\.+/, '')
    .slice(0, 120);
  return cleaned.length > 0 ? cleaned : 'file';
}

export function attachmentKey(
  entityType: string,
  entityId: string,
  objectId: string,
  fileName: string,
): string {
  return `attachments/${entityType}/${entityId}/${objectId}/${sanitizeFileName(fileName)}`;
}
